import React, { useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { FileCheck } from 'lucide-react'
import { useFinalizeInvoice } from '@data/subscriptions/subscriptions'
import { getBillingApiError } from '@data/billing/billing'

/**
 * Just the fields this modal needs, so it can be opened from the Invoices tab
 * (where a full IInvoiceRow is on hand) and from the Devices tab (where only
 * the device's held draft is known) without either side fabricating a row.
 */
export interface FinalizableInvoice {
  invoiceId: string
  requiresPo: boolean
  poNumber: string | null
  /** Official invoice number; null while the invoice is still a draft. */
  number: string | null
}

interface Props {
  invoice: FinalizableInvoice
  accountId: number
  show: boolean
  onHide: () => void
}

/**
 * Finalizes a draft invoice, optionally attaching a PO number first.
 *
 * Billing entities flagged as requiring an individual PO per invoice get a
 * required PO input; everyone else gets a plain confirmation. The backend
 * flow is the same either way — the PO is simply omitted.
 */
const FinalizeInvoiceModal: React.FC<Props> = ({ invoice, accountId, show, onHide }) => {
  const [poNumber, setPoNumber] = useState(invoice.poNumber ?? '')

  const { mutateAsync: finalizeInvoice, isPending, error } = useFinalizeInvoice(accountId)

  const requiresPo = invoice.requiresPo
  const invoiceLabel = invoice.number ?? invoice.invoiceId
  // Field-scoped errors (e.g. a rejected PO number) render inline rather than
  // as a toast — matching the convention in BillingEntityModal.
  const apiError = getBillingApiError(error)
  const poError = apiError?.field === 'po_number' ? apiError.message : undefined

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = poNumber.trim()
    try {
      await finalizeInvoice({
        invoice_id: invoice.invoiceId,
        ...(requiresPo && trimmed ? { po_number: trimmed } : {}),
      })
    } catch {
      // The error is already surfaced by the mutation — inline for field
      // errors, a toast otherwise. Swallow it here so the rejection is not
      // unhandled, and leave the modal open so the user can correct and retry.
      return
    }
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="text-uppercase">
            {requiresPo ? 'Add PO to Invoice' : 'Finalize Invoice'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requiresPo ? (
            <>
              <p className="text-muted mb-3">
                Please provide a purchase order reference for invoice <span className="fw-semibold">{invoiceLabel}</span> to finalize it.
              </p>
              <Form.Group className="mb-2">
                <Form.Label className="text-muted small fw-semibold text-uppercase">
                  PO Number
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  maxLength={140}
                  autoFocus
                  isInvalid={!!poError}
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="e.g. PO-2026-00123"
                />
                <Form.Control.Feedback type="invalid">{poError}</Form.Control.Feedback>
              </Form.Group>
            </>
          ) : (
            <p className="text-muted mb-0">
              Finalize invoice <span className="fw-semibold">{invoiceLabel}</span>? 
              Once finalized the invoice can no longer be edited.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                {requiresPo ? 'Add PO & finalize' : 'Finalize invoice'}{' '}
                <FileCheck size={14} className="ms-1" />
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default FinalizeInvoiceModal
